import { useState } from "react";
import { Eyebrow } from "@/components/media/Sections";
import { embedUrl } from "@/components/media/VideoEmbed";
import { ExternalLink, Instagram, Play } from "lucide-react";

const Post = ({ post }) => {
  const [playing, setPlaying] = useState(false);
  const embed = embedUrl(post.url);
  const isReel = /\/reels?\//i.test(post.url);

  return (
    <article
      data-testid={`ig-post-${post.id}`}
      className="group flex flex-col border border-border bg-[#141414] transition-colors hover:border-primary/60"
    >
      {embed && playing ? (
        <iframe
          data-testid={`ig-iframe-${post.id}`}
          src={embed}
          title={post.caption || "Instagram post"}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          scrolling="no"
          className="w-full border-0 bg-black"
          style={{ aspectRatio: isReel ? "9/16" : "4/5" }}
        />
      ) : (
        <button
          data-testid={`ig-open-${post.id}`}
          onClick={() => (embed ? setPlaying(true) : window.open(post.url, "_blank"))}
          className="relative w-full overflow-hidden bg-black"
          style={{ aspectRatio: isReel ? "9/16" : "4/5" }}
        >
          {post.thumbnail ? (
            <img src={post.thumbnail} alt="" loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <span className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,#2a2a12,#0a0a0a)]" />
          )}
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform duration-300 group-hover:scale-110">
              {isReel ? <Play size={20} fill="currentColor" /> : <Instagram size={20} />}
            </span>
          </span>
        </button>
      )}
      <div className="flex flex-1 flex-col p-4">
        {post.caption ? <p className="text-sm leading-snug text-white">{post.caption}</p> : null}
        <a
          href={post.url}
          target="_blank"
          rel="noreferrer"
          data-testid={`ig-link-${post.id}`}
          className="mono mt-auto inline-flex items-center gap-1.5 pt-4 text-[10px] tracking-widest text-primary hover:underline"
        >
          VIEW ON INSTAGRAM <ExternalLink size={11} />
        </a>
      </div>
    </article>
  );
};

/** Instagram wall for the customer side — embeds, served by Instagram. */
export const InstagramWall = ({ wall }) => {
  if (!wall) return null;
  const posts = wall.posts || [];

  return (
    <>
      <Eyebrow>FROM THE FEED</Eyebrow>
      <h2 className="neon mt-3 max-w-3xl text-4xl leading-tight sm:text-5xl">{wall.title}</h2>
      <p className="mt-4 max-w-2xl text-base text-muted-foreground md:text-lg">{wall.intro}</p>

      {posts.length ? (
        <div className="mt-10 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {posts.map((p) => (
            <Post key={p.id} post={p} />
          ))}
        </div>
      ) : null}

      <a
        href={wall.handle_url || "#"}
        target="_blank"
        rel="noreferrer"
        data-testid="ig-wall-cta"
        className="display mt-10 inline-flex items-center gap-2 rounded-sm border border-primary px-6 py-3.5 text-lg uppercase text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
      >
        <Instagram size={17} />
        {wall.cta || "See the full feed on Instagram"}
        <span className="mono text-[11px] tracking-widest">{wall.handle}</span>
      </a>
    </>
  );
};
